import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import eventsApi from '../api/eventsApi';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations/translations';
import '../styles/cart.css';
import '../styles/cart-mobile.css';

const Cart = () => {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();

  // Function to translate date to Polish
  const translateDate = (dateString) => {
    if (language === 'pl') {
      // Convert "October 31" to "31 Października"
      const monthTranslations = {
        'January': 'Stycznia',
        'February': 'Lutego', 
        'March': 'Marca',
        'April': 'Kwietnia',
        'May': 'Maja',
        'June': 'Czerwca',
        'July': 'Lipca',
        'August': 'Sierpnia',
        'September': 'Września',
        'October': 'Października',
        'November': 'Listopada',
        'December': 'Grudnia'
      };
      
      // Replace month names with Polish equivalents
      let translatedDate = dateString;
      Object.entries(monthTranslations).forEach(([en, pl]) => {
        translatedDate = translatedDate.replace(en, pl);
      });
      
      // Convert "31 Października" format (day month) from "October 31" (month day)
      const parts = translatedDate.split(' ');
      if (parts.length === 2) {
        return `${parts[1]} ${parts[0]}`;
      }
      
      return translatedDate;
    }
    return dateString;
  };
  // eslint-disable-next-line no-unused-vars
  const [selectedEvent, setSelectedEvent] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [ticketQuantity, setTicketQuantity] = useState(1);
  const [attendees, setAttendees] = useState([]);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [cartEvents, setCartEvents] = useState([]);
  const [currentStep, setCurrentStep] = useState(1); // 1 = event/attendee selection, 2 = payment
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [paymentInfo, setPaymentInfo] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
    billingAddress: '',
    city: '',
    postalCode: '',
    country: ''
  });

  // Load events from MongoDB (language-aware)
  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoadingEvents(true);
        const eventsData = await eventsApi.getAllEvents(language);
        
        // Filter out past events
        const now = new Date();
        const upcomingEvents = eventsData.filter(event => {
          const eventDate = new Date(event.date);
          return eventDate >= now;
        });
        
        // Transform MongoDB events to match cart structure
        const formattedEvents = upcomingEvents.map(event => ({
          id: event._id,
          title: event.title,
          date: new Date(event.date).toLocaleDateString('en-US', { day: '2-digit', month: 'long' }),
          price: event.price,
          available: event.availableSeats,
          image: event.thumbnail || "/assets/default-event.jpg",
          description: event.description || event.shortDescription,
          currency: event.currency || 'PLN'
        }));
        setEvents(formattedEvents);
      } catch (err) {
        console.error('Failed to load events:', err);
      } finally {
        setLoadingEvents(false);
      }
    };
    loadEvents();
  }, [language]); // Reload when language changes

  // Set initial selected event from URL parameter
  useEffect(() => {
    if (loadingEvents || events.length === 0) return;
    
    const eventId = searchParams.get('event');
    if (eventId) {
      setSelectedEvent(eventId);
      const event = events.find(e => e.id === eventId);
      if (event) {
        setCartEvents([{ ...event, quantity: 1 }]);
      }
    }
  }, [searchParams, events, loadingEvents]);

  // Update attendees when cart events change
  useEffect(() => {
    const totalTickets = cartEvents.reduce((sum, event) => sum + event.quantity, 0);
    if (totalTickets > 0) {
      setAttendees(prevAttendees => {
        const newAttendees = [];
        
        cartEvents.forEach(event => {
          for (let i = 0; i < event.quantity; i++) {
            const eventAttendeeNumber = i + 1; // Each event starts from Attendee 1
            
            // Check if this attendee already exists in previous state
            const existingAttendee = prevAttendees.find(attendee => 
              attendee.eventId === event.id && attendee.attendeeNumber === eventAttendeeNumber
            );
            
            if (existingAttendee) {
              // Preserve existing data
              newAttendees.push({
                ...existingAttendee,
                attendeeNumber: eventAttendeeNumber
              });
            } else {
              // Create new attendee
              newAttendees.push({
                eventId: event.id,
                eventTitle: event.title,
                attendeeNumber: eventAttendeeNumber,
                name: '',
                email: ''
              });
            }
          }
        });
        
        return newAttendees;
      });
    } else {
      setAttendees([]);
    }
  }, [cartEvents]);

  // eslint-disable-next-line no-unused-vars
  const selectedEventData = events.find(event => event.id === selectedEvent);
  const subtotal = cartEvents.reduce((sum, event) => sum + (event.price * event.quantity), 0);
  
  // Calculate discount
  let discount = 0;
  if (appliedPromo) {
    if (appliedPromo.discountType === 'percentage') {
      discount = (subtotal * appliedPromo.discountValue) / 100;
    } else if (appliedPromo.discountType === 'fixed') {
      discount = appliedPromo.discountValue;
    }
  }
  
  const total = Math.max(0, subtotal - discount);

  const handleAttendeeChange = (index, field, value) => {
    const newAttendees = [...attendees];
    newAttendees[index][field] = value;
    setAttendees(newAttendees);
  };


  const addEventToCart = (event) => {
    // Check if event is sold out
    if (!event.available || event.available <= 0) {
      alert(t('cart.soldOutEvent', language));
      return;
    }

    const existingEventIndex = cartEvents.findIndex(e => e.id === event.id);
    if (existingEventIndex >= 0) {
      // Update quantity if event already exists, but check available tickets
      const updatedCart = [...cartEvents];
      const newQuantity = updatedCart[existingEventIndex].quantity + 1;
      
      if (newQuantity > event.available) {
        alert(language === 'pl' 
          ? `Tylko ${event.available} biletów jest dostępnych dla tego wydarzenia` 
          : `Only ${event.available} tickets are available for this event`);
        return;
      }
      
      updatedCart[existingEventIndex].quantity = newQuantity;
      setCartEvents(updatedCart);
    } else {
      // Add new event to cart
      setCartEvents([...cartEvents, { ...event, quantity: 1 }]);
    }
    setSelectedEvent(event.id); // Already a string from MongoDB
  };

  const updateEventQuantity = (eventId, quantity) => {
    const event = cartEvents.find(e => e.id === eventId);
    
    // Validate quantity doesn't exceed available seats
    if (quantity > event.available) {
      alert(language === 'pl' 
        ? `Tylko ${event.available} biletów jest dostępnych` 
        : `Only ${event.available} tickets are available`);
      return;
    }
    
    const updatedCart = cartEvents.map(event => 
      event.id === eventId ? { ...event, quantity: quantity } : event
    );
    setCartEvents(updatedCart);
  };

  const removeEventFromCart = (eventId) => {
    const updatedCart = cartEvents.filter(event => event.id !== eventId);
    setCartEvents(updatedCart);
  };

  // eslint-disable-next-line no-unused-vars
  const handlePaymentChange = (field, value) => {
    setPaymentInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoError(t('cart.enterPromo', language));
      return;
    }

    setPromoLoading(true);
    setPromoError('');

    try {
      const response = await fetch('/api/validate-promo-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: promoCode })
      });

      const data = await response.json();

      if (data.valid) {
        setAppliedPromo({
          code: data.code,
          discountType: data.discountType,
          discountValue: data.discountValue
        });
        setPromoError('');
        alert(t('cart.promoApplied', language).replace('{discount}', data.discountValue));
      } else {
        setPromoError(data.error || t('cart.invalidPromo', language));
        setAppliedPromo(null);
      }
    } catch (error) {
      console.error('Error validating promo code:', error);
      setPromoError(t('cart.promoError', language));
      setAppliedPromo(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
    setPromoCode('');
    setPromoError('');
  };

  const proceedToPayment = async () => {
    // Check if there are any attendees
    if (attendees.length === 0) {
      alert(t('cart.selectEventFirst', language));
      return;
    }

    // Validate that all attendee names and emails are filled
    const allAttendeesComplete = attendees.every(attendee => 
      attendee.name.trim() !== '' && attendee.email.trim() !== ''
    );

    if (!allAttendeesComplete) {
      alert(t('cart.fillAttendeeInfo', language));
      return;
    }

    // Proceed directly to Stripe checkout
    await handleSubmit();
  };

  const goBackToEvents = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    // Validate attendee info
    if (attendees.length === 0 || !attendees[0]?.name) {
      alert(t('cart.provideAttendeeInfo', language));
      return;
    }

    setCheckoutLoading(true);

    try {
      // Prepare events data for Stripe Checkout
      const eventsData = cartEvents.map(cartEvent => {
        const eventAttendees = attendees.filter(attendee => attendee.eventId === cartEvent.id);
        return eventAttendees.map(attendee => ({
          eventId: cartEvent.id,
          attendeeName: attendee.name,
          eventTitle: cartEvent.title,
          price: cartEvent.price
        }));
      }).flat();

      console.log('Events data:', eventsData);
      console.log('Total events/attendees:', eventsData.length);
      
      // Get buyer email from the first attendee (they should all be the same)
      const buyerEmail = attendees[0]?.email || '';
      
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          events: eventsData,
          buyerEmail: buyerEmail,
          language: language,
          promoCode: appliedPromo ? {
            code: appliedPromo.code,
            discountType: appliedPromo.discountType,
            discountValue: appliedPromo.discountValue
          } : null
        }),
      });

      console.log('Response status:', response.status);
      
      const responseText = await response.text();
      console.log('Raw response:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse JSON. Response:', responseText);
        throw new Error('Server error occurred. Please check console for details.');
      }
      
      console.log('Response data:', data);

      if (!response.ok) {
        console.error('API Error:', data);
        throw new Error(data.error || data.details || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      alert(t('cart.paymentError', language));
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="cart-page">
      {/* Logo */}
      <div className="cart-logo">
        <Link to="/">
          <img 
            src="/assets/PawilonLogoBlack.svg" 
            alt="Pawilon Logo" 
            className="logo"
          />
        </Link>
      </div>

      <div className="cart-container">
        {/* Main Content - 70% */}
        <div className="cart-main">
          <form onSubmit={handleSubmit} className="cart-form">
            {currentStep === 1 ? (
              <>
            {/* Event Selection */}
            <div className="form-section">
              <div className="form-section-header">
                <h2 className="form-section-title">{t('cart.yourEvent', language)}</h2>
                {!showAllEvents && (
                  <button 
                    type="button"
                    className="add-event-button"
                    onClick={() => setShowAllEvents(true)}
                  >
                    + {t('cart.addAnother', language)}
                  </button>
                )}
              </div>
              
              <div className="event-cards-container">
                {/* Always show cart events */}
                {cartEvents.map(cartEvent => (
                  <div key={cartEvent.id} className="event-card selected">
                    <div className="event-card-image">
                      <img src={cartEvent.image} alt={cartEvent.title} />
                    </div>
                    
                    <div className="event-card-content">
                      <div className="event-card-info">
                        <h3 className="event-card-title">{cartEvent.title}</h3>
                        <p className="event-card-date">{translateDate(cartEvent.date)}</p>
                        <p className="event-card-description">{cartEvent.description}</p>
                        <p className="event-card-price">{cartEvent.price} PLN {t('cart.perTicket', language)}</p>
                      </div>
                      
                      <div className="event-card-actions">
                        <div className="event-card-quantity">
                          <label className="quantity-label">{t('cart.tickets', language)}</label>
                          <div className="quantity-and-remove-mobile">
                            <select
                              value={cartEvent.quantity}
                              onChange={(e) => updateEventQuantity(cartEvent.id, parseInt(e.target.value))}
                              className="quantity-select"
                            >
                              {[...Array(Math.min(10, cartEvent.available || 0))].map((_, i) => (
                                <option key={i + 1} value={i + 1}>{i + 1}</option>
                              ))}
                            </select>
                            <button 
                              type="button"
                              className="remove-event-button"
                              onClick={() => removeEventFromCart(cartEvent.id)}
                            >
                              {t('cart.remove', language)}
                            </button>
                          </div>
                          {cartEvent.available && cartEvent.available <= 5 && (
                            <span className="low-stock-warning">
                              {language === 'pl' ? `Tylko ${cartEvent.available} pozostało!` : `Only ${cartEvent.available} left!`}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Show dropdown when adding more events */}
                {showAllEvents && (
                  <div className="add-event-dropdown">
                    <div className="form-group">
                      <select
                        id="addEventSelect"
                        className="form-select"
                        onChange={(e) => {
                          if (e.target.value) {
                            const event = events.find(ev => ev.id === e.target.value);
                            if (event && event.available > 0) {
                              addEventToCart(event);
                            }
                            e.target.value = ''; // Reset dropdown
                          }
                        }}
                      >
                        <option value="">{t('cart.selectEvent', language)}</option>
                        {events
                          .filter(event => !cartEvents.some(cartEvent => cartEvent.id === event.id))
                          .map(event => (
                            <option 
                              key={event.id} 
                              value={event.id}
                              disabled={!event.available || event.available === 0}
                            >
                              {event.title} - {event.date} - {event.price} PLN {(!event.available || event.available === 0) ? t('cart.soldOut', language) : ''}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
              
              {showAllEvents && (
                <button 
                  type="button"
                  className="done-selecting-button"
                  onClick={() => setShowAllEvents(false)}
                >
                  {t('cart.closeSelector', language)}
                </button>
              )}
            </div>

            {/* Attendee Information */}
            <div className="form-section no-border">
                <h2 className="form-section-title">{t('cart.attendeeInfo', language)}</h2>
              {cartEvents.map(event => {
                const eventAttendees = attendees.filter(attendee => attendee.eventId === event.id);
                return (
                  <div key={event.id} className="event-attendees-group">
                    <h3 className="event-attendees-title">{event.title}</h3>
                    <p className="event-attendees-subtitle">{event.quantity} {event.quantity > 1 ? t('cart.ticketsRequired', language) : t('cart.ticketRequired', language)}</p>
                    {eventAttendees.map((attendee, index) => {
                      const globalIndex = attendees.findIndex(a => a === attendee);
                      return (
                        <div key={globalIndex} className="attendee-group">
                          <h4 className="attendee-title">{t('cart.attendee', language)} {attendee.attendeeNumber}</h4>
                          <div className="form-row">
                            <div className="form-group">
                              <input
                                type="text"
                                id={`name-${globalIndex}`}
                                value={attendee.name}
                                onChange={(e) => handleAttendeeChange(globalIndex, 'name', e.target.value)}
                                className="form-input"
                                placeholder={language === 'pl' ? 'Imię' : 'Name'}
                                required
                              />
                            </div>
                            <div className="form-group">
                              <input
                                type="email"
                                id={`email-${globalIndex}`}
                                value={attendee.email}
                                onChange={(e) => handleAttendeeChange(globalIndex, 'email', e.target.value)}
                                className="form-input"
                                placeholder="Email"
                                required
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
              </>
            ) : (
              <>
                {/* Payment Information - Stripe Checkout */}
                <div className="form-section">
                  <div className="form-section-header">
                    <h2 className="form-section-title">{t('cart.payment', language)}</h2>
                    <button type="button" className="back-button" onClick={goBackToEvents}>
                      ← {t('cart.backToEvents', language)}
                    </button>
                  </div>
                  
                  <div className="payment-summary">
                    <div className="payment-order-summary">
                      <h3>{t('cart.orderSummary', language)}</h3>
                      {cartEvents.map((cartEvent, index) => {
                        const eventAttendees = attendees.filter(attendee => attendee.eventId === cartEvent.id);
                        return (
                          <div key={cartEvent.id} className="payment-event-summary">
                            <div className="payment-event-header">
                              <h4>{cartEvent.title}</h4>
                              <span className="payment-event-price">{cartEvent.price} PLN</span>
                  </div>
                            <div className="payment-attendees">
                              {eventAttendees.map((attendee, attendeeIndex) => (
                                <div key={attendeeIndex} className="payment-attendee">
                                  <span className="attendee-name">{attendee.name}</span>
                                  {attendee.email && (
                                    <span className="attendee-email">({attendee.email})</span>
                                  )}
                    </div>
                              ))}
                    </div>
                  </div>
                        );
                      })}
                      <div className="payment-total">
                        <strong>
                          {t('cart.total', language)}: {total} PLN
                        </strong>
                  </div>
                    </div>
                    
                    <div className="payment-security">
                      <p>🔒 {t('cart.securePayments', language)}</p>
                      <p>{t('cart.acceptCards', language)}</p>
                  </div>

                    <button 
                      type="submit" 
                      className="stripe-checkout-button"
                      disabled={checkoutLoading}
                    >
                      {checkoutLoading ? (
                        t('cart.processing', language)
                      ) : (
                        <>
                          💳 {t('cart.proceedPayment', language)}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </form>
        </div>

        {/* Summary Sidebar - 30% */}
        <div className="cart-summary">
          <div className="summary-content">
                <h2 className="summary-title">{t('cart.orderSummary', language)}</h2>
            
            {cartEvents.length > 0 ? (
              <>
                <div className="summary-events">
                  {cartEvents.map((cartEvent, index) => (
                    <div key={cartEvent.id} className="summary-event">
                      <h3 className="event-title">{cartEvent.title}</h3>
                      <p className="event-date">{translateDate(cartEvent.date)}</p>
                      <p className="event-price">{cartEvent.price} PLN {t('cart.perTicket', language)}</p>
                      <p className="event-quantity">{t('cart.quantity', language)} {cartEvent.quantity}</p>
                      <p className="event-subtotal">{t('cart.subtotal', language)}: {cartEvent.price * cartEvent.quantity} PLN</p>
                    </div>
                  ))}
                </div>

                {/* Promo Code */}
                <div className="promo-code-section">
                  {!appliedPromo ? (
                    <>
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        className="promo-code-input"
                        placeholder={t('cart.enterPromoCode', language)}
                        disabled={promoLoading}
                      />
                      <button 
                        type="button" 
                        className="promo-code-button"
                        onClick={applyPromoCode}
                        disabled={promoLoading}
                      >
                        {promoLoading ? t('cart.checking', language) : t('cart.apply', language)}
                      </button>
                    </>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                      <span style={{ color: '#710014', fontWeight: '600' }}>
                        {appliedPromo.code} ({appliedPromo.discountValue}% {t('cart.off', language)})
                      </span>
                      <button 
                        type="button" 
                        className="promo-code-button"
                        onClick={removePromoCode}
                        style={{ marginLeft: 'auto' }}
                      >
                        {t('cart.remove', language)}
                      </button>
                    </div>
                  )}
                  {promoError && (
                    <p style={{ color: '#d32f2f', fontSize: '0.875rem', marginTop: '5px' }}>
                      {promoError}
                    </p>
                  )}
                </div>

                <div className="summary-breakdown">
                  <div className="summary-row">
                    <span>{t('cart.subtotal', language)}</span>
                    <span>{subtotal.toFixed(2)} PLN</span>
                  </div>
                  {appliedPromo && discount > 0 && (
                    <div className="summary-row" style={{ color: '#710014' }}>
                      <span>{t('cart.discount', language)} ({appliedPromo.discountValue}%)</span>
                      <span>-{discount.toFixed(2)} PLN</span>
                    </div>
                  )}
                  <div className="summary-row total">
                    <span>{t('cart.total', language)}</span>
                    <span>{total.toFixed(2)} PLN</span>
                  </div>
                </div>

                <button 
                  type="button" 
                  className="submit-btn" 
                  onClick={proceedToPayment}
                  disabled={checkoutLoading}
                >
                  {checkoutLoading 
                    ? t('cart.loading', language)
                    : t('cart.proceedPayment', language)
                  }
                  </button>
              </>
            ) : (
              <p className="no-event-selected">{t('cart.selectEventToSeeDetails', language)}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;